type BrowserGlobal = typeof globalThis & {
  fetch: typeof fetch;
  location: {
    origin: string;
    href: string;
    host: string;
  };
};

function getBrowserGlobal(): BrowserGlobal | undefined {
  const candidate = globalThis as Partial<BrowserGlobal>;
  if (!candidate.location || typeof candidate.fetch !== 'function') {
    return undefined;
  }

  return candidate as BrowserGlobal;
}

function shouldAddForwardedHost(
  url: URL,
  method: string,
  browserGlobal: BrowserGlobal
): boolean {
  if (url.origin !== browserGlobal.location.origin) {
    return false;
  }

  if (method === 'GET' || method === 'HEAD') {
    return false;
  }

  return url.pathname.endsWith('.data') || url.searchParams.has('_data');
}

function resolveRawUrl(input: RequestInfo | URL): string | undefined {
  if (input == null) return undefined;
  if (input instanceof Request) return input.url;
  if (input instanceof URL) return input.href;
  if (typeof input === 'string') return input;
  // Some libraries (RUM/OTEL wrappers) may pass exotic objects; try toString().
  try {
    const asString = String(input);
    return asString && asString !== '[object Object]' ? asString : undefined;
  } catch {
    return undefined;
  }
}

export function installForwardedHostForRemixDataRequests() {
  const browserGlobal = getBrowserGlobal();
  if (!browserGlobal) {
    return;
  }

  const originalFetch = browserGlobal.fetch.bind(browserGlobal);

  browserGlobal.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    // The shim must be transparent: if anything unexpected happens here, we
    // delegate to the original fetch instead of throwing inside an event
    // handler (which bypasses Remix ErrorBoundary and surfaces the default
    // "Application Error" page).
    try {
      const rawUrl = resolveRawUrl(input);
      if (rawUrl === undefined) {
        return originalFetch(input, init);
      }

      let url: URL;
      try {
        url = new URL(rawUrl, browserGlobal.location.href);
      } catch {
        return originalFetch(input, init);
      }

      const method = (
        init?.method ?? (input instanceof Request ? input.method : 'GET')
      )
        .toString()
        .toUpperCase();

      if (!shouldAddForwardedHost(url, method, browserGlobal)) {
        return originalFetch(input, init);
      }

      if (input instanceof Request) {
        const headers = new Headers(input.headers);
        if (init?.headers) {
          new Headers(init.headers).forEach((value, key) => {
            headers.set(key, value);
          });
        }

        if (!headers.has('x-forwarded-host')) {
          headers.set('x-forwarded-host', browserGlobal.location.host);
        }

        return originalFetch(input, {
          ...init,
          headers,
        });
      }

      const headers = new Headers(init?.headers);
      if (!headers.has('x-forwarded-host')) {
        headers.set('x-forwarded-host', browserGlobal.location.host);
      }

      return originalFetch(input, {
        ...init,
        headers,
      });
    } catch {
      return originalFetch(input, init);
    }
  };
}

type ClientErrorLogger = {
  error: (message: string, payload?: Record<string, unknown>) => void;
};

type WindowWithListeners = {
  addEventListener: (type: string, handler: (event: never) => void) => void;
  removeEventListener: (type: string, handler: (event: never) => void) => void;
};

function getEventWindow(): WindowWithListeners | undefined {
  const candidate = (globalThis as { window?: unknown }).window;
  if (
    !candidate ||
    typeof (candidate as { addEventListener?: unknown }).addEventListener !==
      'function'
  ) {
    return undefined;
  }
  return candidate as unknown as WindowWithListeners;
}

function serializeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }
  if (typeof error === 'string') {
    return { message: error };
  }
  try {
    return { value: JSON.stringify(error) };
  } catch {
    return { value: String(error) };
  }
}

/**
 * Installs `error` and `unhandledrejection` listeners on `window` so that
 * client-side errors thrown outside React's render pass (event handlers,
 * timers, third-party shims) are reported through our logger instead of
 * surfacing as Remix's default "Application Error" page with no telemetry.
 *
 * Safe to call once during entry.client bootstrap. Returns a cleanup function
 * primarily for tests.
 */
export function installGlobalClientErrorHandlers(
  logger: ClientErrorLogger
): () => void {
  const eventWindow = getEventWindow();
  if (!eventWindow) {
    return () => undefined;
  }

  const onError = (event: ErrorEvent) => {
    try {
      logger.error('Unhandled client error', {
        ...serializeError(event.error ?? event.message),
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    } catch {
      // Never let the handler itself throw.
    }
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    try {
      logger.error('Unhandled promise rejection', serializeError(event.reason));
    } catch {
      // Never let the handler itself throw.
    }
  };

  eventWindow.addEventListener('error', onError);
  eventWindow.addEventListener('unhandledrejection', onRejection);

  return () => {
    eventWindow.removeEventListener('error', onError);
    eventWindow.removeEventListener('unhandledrejection', onRejection);
  };
}
